import { useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  onboardingThemeOverrides,
  onboardingTokens,
} from "../../../features/onboarding/theme";
import { t } from "../../../locales/t";
import wordmark from "../../../assets/images/Logo.svg";

export const DELETION_REQUEST_EMAIL = "info@elevateaviation.ca";

const REQUEST_DETAILS = ["fullName", "accountEmail", "statement"];
const DELETED = ["account", "profile", "demographics", "messages"];
const KEPT = ["transactions"];

const bodySx = { fontSize: "0.9375rem", lineHeight: 1.65 };

/** One card per question the page answers, in the onboarding card style. */
const Section = ({ icon, heading, children }) => (
  <Box
    component="section"
    sx={{
      p: 3,
      borderRadius: "1rem",
      backgroundColor: onboardingTokens.card,
      border: `1.5px solid ${onboardingTokens.track}`,
    }}
  >
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
      <Box
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "0.75rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: onboardingTokens.lightTeal,
          color: onboardingTokens.navy,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h2" sx={{ fontSize: "1.125rem", lineHeight: 1.3 }}>
        {heading}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const BulletList = ({ namespace, keys }) => (
  <Box
    component="ul"
    sx={{
      m: 0,
      pl: 2.5,
      "& li + li": { mt: 0.5 },
      "& li::marker": { color: onboardingTokens.teal },
    }}
  >
    {keys.map((key) => (
      <Typography key={key} component="li" sx={bodySx}>
        {t(`deleteAccount.${namespace}.${key}`)}
      </Typography>
    ))}
  </Box>
);

// Linked from the Google Play store listing as the app's delete account URL, so
// it has to stay reachable without a session and must not depend on the API.
const DeleteAccountPage = () => {
  const theme = useMemo(() => createTheme(onboardingThemeOverrides), []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t("deleteAccount.documentTitle");

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: onboardingTokens.background,
        }}
      >
        <Box
          component="header"
          sx={{ borderBottom: `1px solid ${onboardingTokens.track}` }}
        >
          <Container maxWidth="sm" sx={{ pt: 2, pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 44 }}>
              <Box
                component="img"
                src={wordmark}
                alt={t("deleteAccount.logoAlt")}
                sx={{ height: { xs: 26, sm: 32 }, width: "auto", mx: "auto" }}
              />
            </Box>
          </Container>
        </Box>

        <Container component="main" maxWidth="sm" sx={{ pt: 4, pb: 6 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h1"
              sx={{ lineHeight: 1.18, letterSpacing: "-0.01em", mb: 1.75 }}
            >
              {t("deleteAccount.heading")}
            </Typography>
            <Typography
              sx={{ ...bodySx, color: onboardingTokens.mutedForeground }}
            >
              {t("deleteAccount.intro")}
            </Typography>
          </Box>

          <Stack gap={1.75}>
            <Section
              icon={<MailOutlineIcon sx={{ fontSize: 26 }} />}
              heading={t("deleteAccount.request.heading")}
            >
              <Typography sx={{ ...bodySx, mb: 1 }}>
                {t("deleteAccount.request.lead")}{" "}
                {/* Navy rather than the teal onboarding uses for links: this
                    address is the one thing the page exists to give, and teal
                    on white is 2.27:1. */}
                <Box
                  component="a"
                  href={`mailto:${DELETION_REQUEST_EMAIL}`}
                  sx={{
                    color: onboardingTokens.navy,
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    textDecorationColor: onboardingTokens.teal,
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "3px",
                    "&:focus-visible": {
                      outline: `2px solid ${onboardingTokens.teal}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {DELETION_REQUEST_EMAIL}
                </Box>{" "}
                {t("deleteAccount.request.leadEnd")}
              </Typography>
              <BulletList namespace="request" keys={REQUEST_DETAILS} />
              <Typography
                sx={{
                  ...bodySx,
                  mt: 2,
                  color: onboardingTokens.mutedForeground,
                }}
              >
                {t("deleteAccount.request.confirmation")}
              </Typography>
            </Section>

            <Section
              icon={<DeleteOutlineIcon sx={{ fontSize: 26 }} />}
              heading={t("deleteAccount.deleted.heading")}
            >
              <BulletList namespace="deleted" keys={DELETED} />
            </Section>

            <Section
              icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 26 }} />}
              heading={t("deleteAccount.kept.heading")}
            >
              <BulletList namespace="kept" keys={KEPT} />
            </Section>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DeleteAccountPage;
